/**
 * ui.ts — the right-hand design panel (spec §9 step 2). Owns the Chains | Charms
 * tabs and the Chains-tab controls: chain selector, length input (with tier
 * pricing feedback), and closure options. The Charms tab hosts the charm library
 * built by canvas.ts.
 *
 * Styling: Tailwind utility classes (theme tokens — text-foreground / bg-detail
 * etc.), matching the rest of the theme's components. Only the bespoke canvas
 * geometry (charms/ring) stays as scoped `cfg__*` CSS in the section stylesheet.
 *
 * All controls write to the shared build state and call `onChange()` so the
 * canvas re-renders the ring + running total live. Step navigation and charm
 * filters are not built here yet.
 */

import {
  setChain, setClosure, setLength,
  variantsForBase, lengthInputRule, lengthSurcharge, defaultLength, BASE_LAYOUT,
  type BuildState,
} from './state';
import {
  closuresForChain, toClosureSelection, canonicalKey, MULTI_KEY, paletteForBase, comboForChain,
  type Catalog, type ChainCatalogItem, type ClosureItem, type PaletteSet,
} from './data';
import { colorStyle } from './colors';
import { mountScrollbar } from './vscroll';

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

// Self-contained waterproof badge (dark rounded square + light-blue droplet).
const WATERPROOF_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <rect width="16" height="16" rx="2" fill="#232323"/>
  <path d="M10.1562 4.48828C9.57724 3.81958 8.92557 3.21743 8.21328 2.69297C8.15023 2.6488 8.07511 2.62511 7.99813 2.62511C7.92114 2.62511 7.84602 2.6488 7.78297 2.69297C7.07199 3.21765 6.42159 3.81979 5.84375 4.48828C4.55516 5.96813 3.875 7.52813 3.875 9C3.875 10.094 4.3096 11.1432 5.08318 11.9168C5.85677 12.6904 6.90598 13.125 8 13.125C9.09402 13.125 10.1432 12.6904 10.9168 11.9168C11.6904 11.1432 12.125 10.094 12.125 9C12.125 7.52813 11.4448 5.96813 10.1562 4.48828Z" fill="#C8D7EA"/>
</svg>`;

// ── Shared class strings (Tailwind, theme tokens) ───────────────────────────
// Mobile: tabs are a full-width segmented control at the top of the drawer; desktop:
// the vertical tab column attached to the card's left edge.
const TAB_BASE   = 'px-3 py-2 p overflow-hidden flex-1 text-center rounded-t-xs md:flex-none md:text-left md:rounded-xs';
const TAB_ACTIVE = 'bg-detail/10';
const TAB_IDLE   = 'bg-transparent';

const SECTION_TITLE = 'h4 m-0 mb-1';
// Closure chips — same fill pattern as the wb-bookings option lists: a subtle
// bg-detail/10, the selected one bumped to bg-detail/30, animated between states.
// Closures are separate products now; the title carries the metal (e.g. "Toggle Silver")
// but a chain is single-metal so we strip a trailing metal word for the chip label.
const closureLabel = (v: string): string => v.replace(/\s+(rose gold|gold|silver)$/i, '').trim();
// Integer prices show no decimals ("+€5"); fractional show two ("+€2.50").
const plusPrice = (n: number): string => '+€' + (Number.isInteger(n) ? n : n.toFixed(2));
const money = (n: number): string => '€' + (Number.isInteger(n) ? n : n.toFixed(2));

const CHIP_BASE   = 'rounded-xs px-4 py-2 text-sm transition-colors duration-200 ease-out';
const CHIP_IDLE   = 'bg-detail/10 text-foreground hover:bg-detail/20 cursor-pointer';
const CHIP_ACTIVE = 'bg-detail/30 text-foreground cursor-pointer';
const CHIP_SOLD   = 'bg-detail/10 text-foreground/40 cursor-not-allowed line-through';

// Tab ids across both layouts: ring bases use chains|charms; the bag charm (beads
// layout) uses beads|chain|charms (palette grid / length+closure / charms).
export type TabName = 'chains' | 'charms' | 'beads' | 'chain';

export interface MountPanelOpts {
  panel: HTMLElement;          // the right column container
  charmsLibrary: HTMLElement;  // the charm grid built by canvas (Charms tab content)
  catalog: Catalog;
  build: BuildState;
  onChange: () => void;        // canvas re-renders ring + bar after a state change
  onTab?: (name: TabName) => void; // canvas updates its header copy + bar CTA
}

/** Handle the canvas uses to drive the panel (e.g. the bottom bar's "Continue
 *  to charms" button switches the tab; "Restart" re-renders the chain view). */
export interface PanelApi {
  switchTab: (name: TabName) => void;
  refresh: () => void;
}

export function mountPanel({ panel, charmsLibrary, catalog, build, onChange, onTab }: MountPanelOpts): PanelApi {
  panel.innerHTML = '';

  // ── Tabs (vertical, attached to the card's left edge) ─────────────────────
  // Wrapper holds the segmented tabs; on mobile the bottom border is inset (mx-3) so it
  // spans the tabs, not the full sheet width. md:contents flattens the inner on desktop
  // so the buttons become the vertical tab column.
  // The bag charm (beads layout) splits the chain controls into a Beads tab (palette grid)
  // and a Chain tab (length + closure); ring bases keep one Chains tab with everything.
  const isBeads = BASE_LAYOUT[build.base] === 'beads';

  // A scrollable view with its own custom scrollbar (like the original chains view).
  function makeScrollView(): { view: HTMLElement; scroll: HTMLElement; update: () => void } {
    const view = el('div', 'relative h-full min-h-0 overflow-hidden pr-3 transition-opacity duration-150 ease-out');
    const scroll = el('div', 'h-full overflow-y-auto overflow-x-hidden no-scrollbar pl-3 pr-1 pt-3 pb-16 md:pl-4 md:pr-2 md:pt-4');
    view.appendChild(scroll);
    return { view, scroll, update: mountScrollbar(scroll, view, 'y') };
  }

  // Charms fills the card height so its grid can scroll internally (its own scrollbar).
  const charmsView = el('div', 'h-full min-h-0 transition-opacity duration-150 ease-out');
  charmsView.append(charmsLibrary); // heading + filter button live in the library bar (canvas.ts)

  interface TabSpec { name: TabName; label: string; view: HTMLElement }
  let specs: TabSpec[];
  let renderChainsView: () => void; // (re)builds the chain/length/closure content per layout

  if (isBeads) {
    const beads = makeScrollView();
    const chain = makeScrollView();
    renderChainsView = (): void => {
      beads.scroll.innerHTML = '';
      beads.scroll.append(heading('Beads'));
      if (catalog.beadsHelp) beads.scroll.append(copyNote(catalog.beadsHelp));
      // Multi-colour palette (new): a colour-chip selector; else the legacy chain grid.
      beads.scroll.append(paletteForBase(catalog, build.base) ? buildPaletteSelector() : buildChainGrid());
      chain.scroll.innerHTML = '';
      chain.scroll.append(heading('Chain'), lengthSection(), closureSection());
      requestAnimationFrame(() => { beads.update(); chain.update(); });
    };
    specs = [
      { name: 'beads', label: 'Beads', view: beads.view },
      { name: 'chain', label: 'Chain', view: chain.view },
      { name: 'charms', label: 'Charms', view: charmsView },
    ];
  } else {
    const chains = makeScrollView();
    renderChainsView = (): void => {
      chains.scroll.innerHTML = '';
      chains.scroll.append(heading('Chains'), buildChainGrid(), waterproofNote(), lengthSection(), closureSection());
      requestAnimationFrame(chains.update);
    };
    specs = [
      { name: 'chains', label: 'Chains', view: chains.view },
      { name: 'charms', label: 'Charms', view: charmsView },
    ];
  }

  // ── Tabs (vertical, attached to the card's left edge) ─────────────────────
  const tabs = el('div', 'cfg__panel-design w-full shrink-0 pt-1.5 pb-2 bg-background transition-opacity duration-300 md:p-1 md:pb-1 md:w-auto md:rounded-sm md:overflow-hidden md:absolute md:top-0 md:left-0 md:translate-x-[calc(-100%+4px)] md:flex md:flex-col md:gap-0 md:max-h-[calc(100vh-8.5rem)] ');
  const tabsInner = el('div', 'flex gap-1 mx-3 pb-0 border-b border-detail/10 md:contents');
  const tabButtons = new Map<TabName, HTMLButtonElement>();
  for (const s of specs) { const b = tabBtn(s.label); tabButtons.set(s.name, b); tabsInner.appendChild(b); }
  tabs.appendChild(tabsInner);

  // The card is a non-scrolling container; each view scrolls internally.
  const card = el('div', 'cfg__panel-design  flex-1 min-w-0 min-h-0 w-full bg-background rounded-sm rounded-tl-0 overflow-hidden transition-opacity duration-300  md:max-h-[calc(100svh-8.5rem)] md:h-full');
  specs.forEach((s, i) => { if (i > 0) s.view.style.display = 'none'; card.appendChild(s.view); });

  panel.append(tabs, card);

  // Tab views cross-fade: fade the current out, wait 50ms, swap, fade the next in.
  const FADE_MS = 150;
  let visibleView: HTMLElement = specs[0].view;
  let fadeTimer: number | undefined;

  // ── Chains tab ──────────────────────────────────────────────────────────
  // Built ONCE; selections mutate the existing nodes in place (toggle classes)
  // rather than re-rendering, so nothing flashes/reflows and the CSS transitions
  // actually run. Only refresh() (restart) does a full rebuild.
  let cardEls: { id: string; el: HTMLButtonElement }[] = [];
  let closuresWrap: HTMLElement;
  let lengthsWrap: HTMLElement;

  // A short paragraph of merchant-editable copy (shared styling). Empty → caller skips it.
  function copyNote(text: string): HTMLElement {
    const p = el('p', 'p w-[35ch] max-w-full text-foreground m-0 mb-3');
    p.textContent = text;
    return p;
  }

  function buildChainGrid(): HTMLElement {
    const grid = el('div', 'grid grid-cols-2 gap-2');
    cardEls = [];
    // Only chains that have a variant for this base, kept in COLLECTION order (the Liquid
    // emits collection.products in the collection's own sort). A chain with all its base
    // variants out of stock is shown disabled with a "Sold out" badge.
    const chains = catalog.chains.filter(c => c.variants.some(v => v.base === build.base));
    for (const c of chains) {
      const soldOut = !c.variants.some(v => v.base === build.base && v.inventory > 0);
      const card = el('button',
        `group relative aspect-[3/2] rounded-sm overflow-hidden bg-[#F5F5F5] p-0 text-left transition-colors duration-200 ease-out${soldOut ? ' opacity-40 cursor-not-allowed' : ' cursor-pointer'}`) as HTMLButtonElement;
      card.type = 'button';
      if (soldOut) card.disabled = true;
      // Sidebar card uses the close-up texture shot (configurator.chain_close_up_image),
      // NOT the ring PNG the canvas builds with. Falls back to the ring image.
      const cardImg = c.closeUpImage ?? c.image;
      const badge = soldOut
        ? `<span class="absolute right-2 bottom-2 z-[2] product-badge">Sold out</span>`
        : `<span data-role="badge-suggest" class="absolute right-2 bottom-2 z-[2] product-badge opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100">Select chain</span>`;
      // Every state element is ALWAYS in the DOM; updateChainSelection() toggles
      // visibility — so switching chains never re-creates the <img> (no reload/flash).
      // Hover tint = light-blue layer above the card bg, below the (transparent) chain
      // PNG, mix-blend-color so the background showing through picks up the hue.
      card.innerHTML =
        `<span data-role="tint" class="absolute inset-0 bg-[#C8D7EA] mix-blend-color opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100"></span>` +
        (cardImg ? `<img class="absolute inset-0 w-full h-full object-cover" style="object-position:${c.closeUpFocal}" src="${cardImg}" alt="">` : '') +
        `<span class="absolute top-0 left-0 right-0 z-[1] flex flex-col gap-0.5 px-1.5 py-2">` +
          `<span class="h5 text-foreground${isBeads ? ' max-w-[14ch]' : ''}">${isBeads ? c.title.split(' ')[0] : c.title}</span>` +
          `<span class="p text-foreground/60${isBeads ? ' max-w-[14ch]' : ''}">${c.chainType || ''}</span>` +
        `</span>` +
        badge;
      if (!soldOut) card.addEventListener('click', () => selectChain(c));
      cardEls.push({ id: c.id, el: card });
      grid.appendChild(card);
    }
    updateChainSelection();
    return grid;
  }

  // Reflect the selected chain across the cards without re-rendering (no <img> reload).
  // Selected = the blue tint pinned on persistently (opacity-100!); unselected only show
  // it on hover. The 'Select chain' hover badge stays on every card.
  function updateChainSelection(): void {
    for (const { id, el: card } of cardEls) {
      const selected = build.chain?.id === id;
      (card.querySelector('[data-role=tint]') as HTMLElement | null)?.classList.toggle('opacity-100!', selected);
      // The selected card shows a persistent "Selected" badge (the unselected cards show
      // "Select chain" on hover only — which never triggers on touch).
      const badge = card.querySelector('[data-role=badge-suggest]') as HTMLElement | null;
      if (badge) {
        badge.textContent = selected ? 'Selected' : 'Select chain';
        badge.classList.toggle('opacity-100!', selected);
      }
    }
  }

  // ── Bead-palette colour selector (bag charm / keychain) ───────────────────
  // Replaces the chain grid on beads bases: toggle up to 3 colour chips, or the
  // exclusive Multicolour. Each selection resolves to a synthetic combo-chain
  // (data.ts) and flows through the same selectChain() as any normal chain, so
  // canvas/pricing/cart/serialize are unchanged. Selection state is derived from
  // the resolved build.chain (no extra state needed).
  let paletteChipEls: { color: string; el: HTMLButtonElement }[] = [];
  let paletteMultiEl: HTMLButtonElement | null = null;

  const PALETTE_CHIP = 'inline-flex items-center gap-1.5 rounded-xs px-2.5 py-1.75 text-sm transition-colors duration-200 ease-out';
  function paletteChipClass(active: boolean, disabled: boolean): string {
    if (disabled) return `${PALETTE_CHIP} bg-detail/10 text-foreground/40 cursor-not-allowed`;
    return `${PALETTE_CHIP} cursor-pointer text-foreground ${active ? 'bg-detail/30' : 'bg-detail/10 hover:bg-detail/20'}`;
  }
  const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

  function currentPalette(): PaletteSet | undefined { return paletteForBase(catalog, build.base); }
  function selectedColors(): string[] {
    const pal = currentPalette(); if (!pal) return [];
    const combo = comboForChain(pal, build.chain?.id);
    return combo && !combo.isMulti ? combo.colors : [];
  }
  function isMultiSelected(): boolean {
    const pal = currentPalette(); if (!pal) return false;
    return !!comboForChain(pal, build.chain?.id)?.isMulti;
  }

  function buildPaletteSelector(): HTMLElement {
    const pal = currentPalette()!;
    const wrap = el('div', 'flex flex-col gap-2');
    const row = el('div', 'flex flex-wrap gap-1.5');
    paletteChipEls = [];
    for (const label of pal.colors) {
      const b = el('button', paletteChipClass(false, false)) as HTMLButtonElement;
      b.type = 'button';
      b.innerHTML =
        `<span class="w-3.5 h-3.5 rounded-full border border-foreground/20 shrink-0" style="${colorStyle(label)}"></span>` +
        `<span>${capitalize(label)}</span>`;
      b.addEventListener('click', () => toggleColor(label));
      paletteChipEls.push({ color: label.toLowerCase(), el: b });
      row.appendChild(b);
    }
    wrap.appendChild(row);
    // The exclusive Multicolour chip — only if that combo has been authored.
    if (pal.comboByKey.has(MULTI_KEY)) {
      const m = el('button', `${paletteChipClass(false, false)} self-start`) as HTMLButtonElement;
      m.type = 'button';
      m.innerHTML =
        `<span class="w-3.5 h-3.5 rounded-full border border-foreground/20 shrink-0" style="${colorStyle('multi')}"></span>` +
        `<span>Multicolour</span>`;
      m.addEventListener('click', toggleMulti);
      paletteMultiEl = m;
      wrap.appendChild(m);
    } else {
      paletteMultiEl = null;
    }
    updatePaletteSelection();
    return wrap;
  }

  function resolvePalette(colors: string[], multi: boolean): void {
    const pal = currentPalette(); if (!pal) return;
    const combo = pal.comboByKey.get(multi ? MULTI_KEY : canonicalKey(colors));
    if (!combo) return;            // no image-backed combo (full coverage → shouldn't happen)
    selectChain(combo.chain);      // chain + reprice + closures/lengths + onChange
    updatePaletteSelection();
  }

  function toggleColor(label: string): void {
    const nk = label.toLowerCase();
    const cur = selectedColors();
    let next: string[];
    if (cur.includes(nk)) {
      next = cur.filter(c => c !== nk);
      if (next.length === 0) return;   // keep at least one colour
    } else {
      if (cur.length >= 3) return;     // max 3
      next = [...cur, nk];
    }
    resolvePalette(next, false);
  }

  function toggleMulti(): void {
    if (isMultiSelected()) return;
    resolvePalette([], true);
  }

  function updatePaletteSelection(): void {
    const multi = isMultiSelected();
    const cur = selectedColors();
    const atMax = cur.length >= 3;
    for (const { color, el: b } of paletteChipEls) {
      const active = !multi && cur.includes(color);
      const disabled = multi || (!active && atMax);
      b.className = paletteChipClass(active, disabled);
      b.disabled = disabled;
    }
    if (paletteMultiEl) paletteMultiEl.className = `${paletteChipClass(multi, false)} self-start`;
  }

  function selectChain(c: ChainCatalogItem): void {
    setChain(build, c);                 // swaps the ring image (via onChange) + reprices
    ensureClosureValid();               // a new chain may offer different closures
    ensureLengthInBounds();             // …and a different length range
    onChange();
    updateChainSelection();             // in-place: toggle card state (no <img> reload)
    renderLengths();                    // the available length tiers differ per chain
    renderClosures();                   // closures can differ per chain
  }

  // Waterproof note, shown below the chain grid.
  function waterproofNote(): HTMLElement {
    const p = el('p', 'p inline-flex items-center gap-1.5 m-0 mt-3');
    p.innerHTML = `<span class="inline-flex w-4 h-4 shrink-0" aria-hidden="true">${WATERPROOF_SVG}</span> All our chains are waterproof.`;
    return p;
  }

  function ensureClosureValid(): void {
    const list = closuresForChain(catalog, build.chain);
    const current = build.closure ? list.find(c => c.id === build.closure!.id && c.available > 0) : undefined;
    if (current) return;                          // still offered & in stock
    const first = list.find(c => c.available > 0) ?? null;
    setClosure(build, first ? toClosureSelection(first) : null);
  }

  // The discrete length tiers offered by the current chain+base (the product's "Length"
  // options), de-duplicated and sorted ascending. The picker only offers these values.
  function availableLengths(): number[] {
    // Only real tiers (> 0). A palette with no Length option (e.g. keychain) has none → no picker.
    const set = new Set(variantsForBase(build.chain, build.base).map(v => v.lengthCm).filter(l => l > 0));
    return Array.from(set).sort((a, b) => a - b);
  }

  // Keep build.length valid for the current chain+base.
  function ensureLengthInBounds(): void {
    // Decimal-input model (bracelet/necklace): clamp to [min,max], default to the base/standard.
    const rule = lengthInputRule(build.chain, build.base);
    if (rule) {
      if (build.length == null || build.length < rule.min || build.length > rule.max) {
        setLength(build, defaultLength(build.base, { min: rule.min, max: rule.max }));
      }
      return;
    }
    const lengths = availableLengths();
    if (lengths.length === 0) return;
    if (build.length != null && lengths.includes(build.length)) return;
    const target = build.length == null
      ? lengths[0]
      : lengths.reduce((best, cm) => Math.abs(cm - build.length!) < Math.abs(best - build.length!) ? cm : best, lengths[0]);
    setLength(build, target);
  }

  function lengthChipClass(active: boolean): string {
    return `${CHIP_BASE} ${active ? CHIP_ACTIVE : CHIP_IDLE}`;
  }

  // Decimal length input (bracelet/necklace): a bounded number field + a live note. Necklaces
  // show the per-cm surcharge above the base length; everything else is a fixed cut size.
  function lengthInputSection(rule: { min: number; max: number; base?: number }): HTMLElement {
    const section = el('div', 'mt-[26px]');
    section.append(sectionTitle('Chain length'));
    const lengthHelp = catalog.lengthHelp[build.base];
    if (lengthHelp) section.append(copyNote(lengthHelp));

    const row = el('div', 'flex items-center gap-2.5');
    const field = el('div', 'inline-flex items-center w-[150px] rounded-xs bg-detail/10 overflow-hidden');
    const input = el('input', 'cfg__length-input flex-1 min-w-0 border-0 bg-transparent text-foreground text-sm leading-tight py-2 pl-3 pr-1 outline-none') as HTMLInputElement;
    input.type = 'number'; input.min = String(rule.min); input.max = String(rule.max); input.step = '1'; input.inputMode = 'numeric';
    input.placeholder = `${rule.min}–${rule.max}`;
    const suffix = el('span', 'shrink-0 pr-3 pl-1 text-sm text-detail pointer-events-none'); suffix.textContent = 'cm';
    field.append(input, suffix); row.append(field);
    const fb = el('p', 'p text-foreground/60 m-0 mt-2 min-h-[1.25em] text-xs');
    section.append(row, fb);

    ensureLengthInBounds(); // valid default (base/standard length)
    input.value = build.length != null ? String(build.length) : '';

    const errCls = 'p m-0 mt-2 min-h-[1.25em] text-xs text-[#b45309]';
    const noteCls = 'p text-foreground/60 m-0 mt-2 min-h-[1.25em] text-xs';
    const surchargeText = (): string => {
      const per = build.chain?.lengthRules?.surchargePerCm ?? 0;
      if (rule.base == null || per <= 0) return ''; // bracelet: just a cut size, no surcharge
      const extra = lengthSurcharge(build);
      return extra > 0
        ? `Includes +${money(extra)} (${(build.length! - rule.base).toFixed(1).replace(/\.0$/, '')}cm over ${rule.base}cm).`
        : `Up to ${rule.base}cm included · +${money(per)}/cm above.`;
    };
    fb.className = noteCls; fb.textContent = surchargeText();

    input.addEventListener('input', () => {
      const raw = input.value.trim();
      if (raw === '') { fb.className = noteCls; fb.textContent = ''; return; }
      const v = Math.round(parseFloat(raw)); // whole cm → exact per-cm surcharge
      const res = setLength(build, Number.isFinite(v) ? v : null);
      if (!res.ok) { fb.className = errCls; fb.textContent = res.message ?? ''; return; }
      fb.className = noteCls; fb.textContent = surchargeText();
      onChange();
    });
    // Normalise the field to the (rounded, in-bounds) stored value when focus leaves.
    input.addEventListener('change', () => { if (build.length != null) input.value = String(build.length); });
    return section;
  }

  function lengthSection(): HTMLElement {
    // Decimal-length model (bracelet/necklace, from metafields): a number input + surcharge note.
    const rule = lengthInputRule(build.chain, build.base);
    if (rule) return lengthInputSection(rule);
    // Palettes with no Length option (e.g. keychain) have a single size → no length picker.
    if (availableLengths().length === 0) return el('div', 'hidden');
    const section = el('div', 'mt-[26px]');
    section.append(sectionTitle('Chain length'));
    const lengthHelp = catalog.lengthHelp[build.base];
    if (lengthHelp) section.append(copyNote(lengthHelp));
    lengthsWrap = el('div', 'flex flex-wrap gap-1');
    section.append(lengthsWrap);
    ensureLengthInBounds(); // default-select a valid tier so pricing resolves
    renderLengths();
    return section;
  }

  // Rebuild the length chips (the tiers differ per chain). Picking restyles in place.
  function renderLengths(): void {
    if (!lengthsWrap) return;
    lengthsWrap.innerHTML = '';
    for (const cm of availableLengths()) {
      const o = el('button', lengthChipClass(build.length === cm)) as HTMLButtonElement;
      o.type = 'button';
      o.dataset.length = String(cm);
      o.textContent = `${cm}cm`;
      o.addEventListener('click', () => selectLength(cm));
      lengthsWrap.appendChild(o);
    }
  }

  function selectLength(cm: number): void {
    if (!setLength(build, cm).ok) return;
    onChange();
    updateLengthSelection(); // in-place restyle (matches the closure chips)
  }

  function updateLengthSelection(): void {
    for (const o of Array.from(lengthsWrap.children) as HTMLButtonElement[]) {
      o.className = lengthChipClass(Number(o.dataset.length) === build.length);
    }
  }

  function closureSection(): HTMLElement {
    const section = el('div', 'mt-[26px]');
    section.append(sectionTitle('Closure'));
    closuresWrap = el('div', 'flex flex-wrap gap-1');
    section.append(closuresWrap);
    renderClosures();
    return section;
  }

  // Chip HTML: type label + extra cost (when the closure adds to the price), the price
  // dimmed in its own span.
  function closureChipText(cl: ClosureItem): string {
    const label = closureLabel(cl.title);
    return cl.price > 0 ? `${label} <span class="opacity-70">${plusPrice(cl.price)}</span>` : label;
  }

  function chipClass(cl: ClosureItem): string {
    if (cl.available <= 0) return `${CHIP_BASE} ${CHIP_SOLD}`;
    return `${CHIP_BASE} ${build.closure?.id === cl.id ? CHIP_ACTIVE : CHIP_IDLE}`;
  }

  // Rebuild the chip list (closures differ per chain — metal-matched). Picking a closure
  // does NOT rebuild; it only restyles the chips (updateClosureSelection) so the
  // bg-detail transition runs smoothly. Sold-out closures render disabled.
  function renderClosures(): void {
    closuresWrap.innerHTML = '';
    const list = closuresForChain(catalog, build.chain);
    for (const cl of list) {
      const soldOut = cl.available <= 0;
      const o = el('button', chipClass(cl)) as HTMLButtonElement;
      o.type = 'button';
      o.dataset.closure = cl.id;
      o.innerHTML = soldOut ? `${closureLabel(cl.title)} · Sold out` : closureChipText(cl);
      o.disabled = soldOut;
      if (!soldOut) o.addEventListener('click', () => selectClosure(cl));
      closuresWrap.appendChild(o);
    }
  }

  function selectClosure(cl: ClosureItem): void {
    setClosure(build, toClosureSelection(cl));
    onChange();
    updateClosureSelection(); // in-place restyle → animates bg-detail/10 ↔ /30
  }

  function updateClosureSelection(): void {
    const list = closuresForChain(catalog, build.chain);
    for (const o of Array.from(closuresWrap.children) as HTMLButtonElement[]) {
      const cl = list.find(c => c.id === o.dataset.closure);
      if (cl) o.className = chipClass(cl);
    }
  }

  // ── Tab switching (generic over the active tab set) ───────────────────────
  function switchTab(name: TabName): void {
    const spec = specs.find(s => s.name === name) ?? specs[0];
    for (const s of specs) {
      tabButtons.get(s.name)!.className = `${TAB_BASE} ${s.name === spec.name ? TAB_ACTIVE : TAB_IDLE}`;
    }
    onTab?.(spec.name); // let canvas update its header copy + bottom-bar CTA

    const next = spec.view;
    if (next === visibleView) return;
    const prev = visibleView;
    visibleView = next;

    clearTimeout(fadeTimer);
    prev.style.opacity = '0';                 // fade the current view out
    fadeTimer = window.setTimeout(() => {      // …wait for the fade + 50ms, then swap
      prev.style.display = 'none';
      next.style.opacity = '0';
      next.style.display = '';
      void next.offsetWidth;                   // reflow so the fade-in transitions
      next.style.opacity = '1';
    }, FADE_MS + 50);
  }
  for (const s of specs) tabButtons.get(s.name)!.addEventListener('click', () => switchTab(s.name));

  renderChainsView();
  switchTab(specs[0].name); // default to the first tab (Chains / Beads)

  return { switchTab, refresh: renderChainsView };
}

// ─── small DOM helpers ────────────────────────────────────────────────────────

function tabBtn(label: string): HTMLButtonElement {
  const b = el('button', `${TAB_BASE} ${TAB_IDLE}`) as HTMLButtonElement;
  b.type = 'button';
  b.textContent = label;
  return b;
}

function heading(text: string): HTMLElement {
  // On mobile the drawer tabs ARE the section titles, so the inner heading is hidden.
  const h = el('h3', 'h3 m-0 mb-[18px] hidden md:block');
  h.textContent = text.toUpperCase();
  return h;
}

function sectionTitle(text: string): HTMLElement {
  const h = el('h4', SECTION_TITLE);
  h.textContent = text.toUpperCase();
  return h;
}
